import { buildPreviewTechnicianNameMap } from "./technician-names";
import type {
  IStAdjustmentGroup,
  IStAdjustmentsSummaryResponse,
  StAdjustmentsPreviewLine,
  StAdjustmentsPreviewUser,
} from "./types";

const MIN_PREVIEW_LINES = 16;
const PREVIEW_LINE_RANGE = 9;
const MIN_PREVIEW_AMOUNT_CENTS = 150_100;
const PREVIEW_AMOUNT_RANGE_CENTS = 350_000;
const PREVIEW_MEMO_PREFIX = "VOLCA-AUTO | ";

const stableRandom = (seed: string) => {
  let hash = 2_166_136_261;

  for (const character of seed) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }

  return hash >>> 0;
};

const previewGroupFor = (
  employeeId: number,
  name: string,
): IStAdjustmentGroup => {
  const pending =
    MIN_PREVIEW_LINES +
    (stableRandom(`${employeeId}:${name}:lines`) % PREVIEW_LINE_RANGE);
  const pendingAmountCents =
    MIN_PREVIEW_AMOUNT_CENTS +
    (stableRandom(`${employeeId}:${name}:amount`) % PREVIEW_AMOUNT_RANGE_CENTS);

  return {
    employee_id: employeeId,
    count: pending,
    amount: pendingAmountCents / 100,
    amount_cents: pendingAmountCents,
    pending_amount_cents: pendingAmountCents,
    pushed: 0,
    pending,
  };
};

export const buildStAdjustmentsPreviewData = (
  users: StAdjustmentsPreviewUser[] | undefined,
) => {
  const technicianNames = buildPreviewTechnicianNameMap(users);
  const groups = Array.from(technicianNames, ([employeeId, name]) =>
    previewGroupFor(employeeId, name),
  );

  const totals = groups.reduce(
    (result, group) => ({
      pending: result.pending + group.pending,
      pendingAmountCents:
        result.pendingAmountCents + group.pending_amount_cents,
    }),
    { pending: 0, pendingAmountCents: 0 },
  );

  const summary: IStAdjustmentsSummaryResponse = {
    totals: {
      count: totals.pending,
      amount: totals.pendingAmountCents / 100,
      amount_cents: totals.pendingAmountCents,
      pushed: 0,
      pending: totals.pending,
      pushed_amount_cents: 0,
      pending_amount_cents: totals.pendingAmountCents,
      pending_amount: totals.pendingAmountCents / 100,
    },
    by_technician: groups,
    warnings: [],
    push_status: {
      status: "idle",
      total: totals.pending,
      pushed: 0,
      skipped: 0,
      failed: 0,
      failures: [],
    },
  };

  return { summary, technicianNames };
};

const PREVIEW_CATEGORIES = [
  "Installation commission",
  "Service commission",
  "Maintenance spiff",
] as const;

const allocatePreviewAmounts = (
  employeeId: number,
  lineCount: number,
  totalAmountCents: number,
) => {
  const weights = Array.from(
    { length: lineCount },
    (_, index) => 100 + (stableRandom(`${employeeId}:${index}:weight`) % 901),
  );
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  const amounts = weights.map((weight) =>
    Math.floor((totalAmountCents * weight) / totalWeight),
  );
  const remainder =
    totalAmountCents - amounts.reduce((sum, amount) => sum + amount, 0);

  return amounts.map((amount, index) => amount + (index < remainder ? 1 : 0));
};

export const stAdjustmentsPreviewLinesFor = (group: IStAdjustmentGroup) => {
  const employeeId = group.employee_id;
  const lineCount = group.pending;
  const totalAmountCents = group.pending_amount_cents;

  if (!employeeId || lineCount <= 0 || totalAmountCents <= 0) return [];

  const amounts = allocatePreviewAmounts(
    employeeId,
    lineCount,
    totalAmountCents,
  );

  return amounts.map<StAdjustmentsPreviewLine>((amountCents, index) => {
    const category = PREVIEW_CATEGORIES[index % PREVIEW_CATEGORIES.length];
    const jobNumber = `${employeeId}${String(index + 1).padStart(2, "0")}`;

    return {
      jobNumber,
      category,
      amountCents,
      memo: `${PREVIEW_MEMO_PREFIX}${category}`,
    };
  });
};
